import type { Metadata } from 'next';
import { PRIMARY_SITE_URL } from '@/config/domain';
import { landingSeo, type LandingSeoEntry } from './landing-seo-data';

export type LandingSection =
  | 'shippers'
  | 'carriers'
  | 'services'
  | 'industries'
  | 'company';

/**
 * Absolute origin for canonicals, OpenGraph URLs and the sitemap. Metadata is rendered on
 * the server, where relative URLs are not enough for crawlers or social scrapers, so this
 * has to resolve to the canonical production origin even in local or preview builds.
 */
export const SITE_URL = PRIMARY_SITE_URL;

export const SITE_NAME = 'Ship Smart Solutions';

export function absoluteSiteUrl(path: string) {
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Social preview image metadata matches the source asset's intrinsic dimensions. */
export const SOCIAL_PREVIEW_IMAGE = {
  url: absoluteSiteUrl('/landing/assets/images/shipsmart/hero-og.jpg'),
  width: 1600,
  height: 918,
  type: 'image/jpeg',
  alt: `${SITE_NAME}, asset-based freight and logistics`,
};

export function landingSeoKey(section: LandingSection, slug?: string) {
  return slug ? `${section}/${slug}` : section;
}

export function isLandingSeoIndexable(entry: LandingSeoEntry) {
  return entry.indexable !== false;
}

export function shouldIncludeLandingSeoInSitemap(
  key: string,
  entry: LandingSeoEntry
) {
  return key !== 'home' && !entry.canonicalOf && isLandingSeoIndexable(entry);
}

export function landingPath(
  locale: string,
  section?: LandingSection,
  slug?: string
) {
  if (!section) return `/${locale}`;
  return `/${locale}/${section}${slug ? `/${slug}` : ''}`;
}

export function buildLandingMetadata({
  locale,
  section,
  slug,
  entry,
  socialImage = SOCIAL_PREVIEW_IMAGE,
}: {
  locale: string;
  section?: LandingSection;
  slug?: string;
  entry: LandingSeoEntry;
  socialImage?: {
    url: string;
    width: number;
    height: number;
    type: string;
    alt: string;
  };
}): Metadata {
  // A duplicate route points its canonical at the primary URL.
  const [canonSection, canonSlug] = entry.canonicalOf
    ? (entry.canonicalOf.split('/') as [LandingSection, string])
    : [section, slug];
  const canonicalPath = landingPath(locale, canonSection, canonSlug);
  const canonicalUrl = absoluteSiteUrl(canonicalPath);
  const { title, description } = entry;
  const socialTitle = entry.socialTitle ?? title;
  const socialDescription = entry.socialDescription ?? description;
  const indexable = isLandingSeoIndexable(entry);

  return {
    metadataBase: new URL(SITE_URL),
    title,
    description,
    alternates: {
      canonical: canonicalUrl,
      languages: {
        'en-US': canonicalUrl,
      },
    },
    openGraph: {
      type: 'website',
      url: canonicalUrl,
      siteName: SITE_NAME,
      title: socialTitle,
      description: socialDescription,
      locale: 'en_US',
      images: [socialImage],
    },
    twitter: {
      card: 'summary_large_image',
      title: socialTitle,
      description: socialDescription,
      images: [socialImage.url],
    },
    robots: {
      index: indexable,
      follow: true,
      googleBot: {
        index: indexable,
        follow: true,
        'max-image-preview': 'large',
        'max-snippet': -1,
        'max-video-preview': -1,
      },
    },
  };
}

/**
 * `generateMetadata` for a landing route. Throws when a route has no SEO entry rather than
 * silently falling back, a page shipping with a generic title is the kind of thing nobody
 * notices until it is already indexed.
 */
export function createStaticLandingMetadata(
  section: LandingSection,
  slug?: string
) {
  return async function generateMetadata({
    params,
  }: {
    params: Promise<{ locale: string }>;
  }): Promise<Metadata> {
    const { locale } = await params;
    const key = landingSeoKey(section, slug);
    const entry = landingSeo[key];

    if (!entry) {
      throw new Error(
        `Missing SEO copy for landing route "${key}". Add it to landing-seo-data.ts.`
      );
    }

    return buildLandingMetadata({ locale, section, slug, entry });
  };
}

export function createHomeLandingMetadata() {
  return async function generateMetadata({
    params,
  }: {
    params: Promise<{ locale: string }>;
  }): Promise<Metadata> {
    const { locale } = await params;
    return buildLandingMetadata({ locale, entry: landingSeo.home });
  };
}
